import type {
  HTMLAttributes,
  ReactNode,
} from 'react'

import {
  cva,
  type VariantProps,
} from 'class-variance-authority'

import { cn } from '@/shared/lib/cn'

const skeletonVariants = cva(
  [
    'relative overflow-hidden',
    'bg-[var(--color-surface-muted)]',
    'animate-pulse',
    'motion-reduce:animate-none',
  ],
  {
    variants: {
      shape: {
        rectangle: 'rounded-md',
        rounded: 'rounded-xl',
        circle: 'rounded-full',
        text: 'rounded',
      },
    },

    defaultVariants: {
      shape: 'rectangle',
    },
  },
)

export interface SkeletonProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {}

export function Skeleton({
  shape,
  className,
  ...props
}: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        skeletonVariants({
          shape,
        }),
        className,
      )}
      {...props}
    />
  )
}

/* -------------------------------------------------------
   Accessible wrapper for a group of skeleton placeholders
------------------------------------------------------- */

export interface SkeletonGroupProps
  extends Omit<
    HTMLAttributes<HTMLDivElement>,
    'children'
  > {
  children: ReactNode
  label?: string
}

export function SkeletonGroup({
  children,
  label = 'Loading content',
  className,
  ...props
}: SkeletonGroupProps) {
  return (
    <div
      {...props}
      aria-busy="true"
      aria-live="polite"
      className={className}
    >
      <span className="sr-only">
        {label}
      </span>

      {children}
    </div>
  )
}

/* -------------------------------------------------------
   Text-line skeleton
------------------------------------------------------- */

export interface SkeletonTextProps
  extends HTMLAttributes<HTMLDivElement> {
  lines?: number
  lastLineWidth?: string
}

export function SkeletonText({
  lines = 3,
  lastLineWidth = '70%',
  className,
  ...props
}: SkeletonTextProps) {
  const safeLineCount =
    Math.max(1, Math.floor(lines))

  return (
    <div
      {...props}
      aria-hidden="true"
      className={cn(
        'space-y-2',
        className,
      )}
    >
      {Array.from({
        length: safeLineCount,
      }).map((_, index) => {
        const isLastLine =
          index === safeLineCount - 1

        return (
          <Skeleton
            key={index}
            shape="text"
            className="h-3"
            style={{
              width: isLastLine
                ? lastLineWidth
                : '100%',
            }}
          />
        )
      })}
    </div>
  )
}